import React, { useState, useMemo } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { TimeOffRequest, TimeOffType, TimeOffStatus } from '../../types'
import { Modal } from '../ui/Modal'
import { Plus, EditIcon, Trash2, CheckCircle, XCircle, Clock } from '../icons/Icons'
import { usePermissions } from '../../hooks/usePermissions'
import { toDateOnly } from '../../utils/validators'
import s from './shared.module.css'

const TYPE_LABELS: Record<TimeOffType, string> = {
  vacation: 'Vacation',
  sick: 'Sick Leave',
  personal: 'Personal',
  unpaid: 'Unpaid Leave',
  other: 'Other',
}

const STATUS_COLORS: Record<TimeOffStatus, { bg: string; color: string }> = {
  pending:  { bg: '#fff4ce', color: '#835b00' },
  approved: { bg: '#e6f4ea', color: '#107c10' },
  rejected: { bg: '#fde7e9', color: '#d13438' },
}

function daysBetween(start: string, end: string): number {
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const s = new Date(Date.UTC(sy, sm - 1, sd))
  const e = new Date(Date.UTC(ey, em - 1, ed))
  return Math.round((e.getTime() - s.getTime()) / 86400000) + 1
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })
}

interface FormState {
  resource_id: string
  time_off_type: TimeOffType
  start_date: string
  end_date: string
  notes: string
  status: TimeOffStatus
}

const BLANK_FORM: FormState = {
  resource_id: '',
  time_off_type: 'vacation',
  start_date: '',
  end_date: '',
  notes: '',
  status: 'pending',
}

export default function TimeOff() {
  const { organization, orgMember, user } = useAuth()
  const { resources, timeOffRequests, orgManagers, refreshData } = useAppData()
  const { isManager, isAdmin } = usePermissions()
  const orgId = organization!.id

  const canManage = isManager || isAdmin

  // For resource-type users: find their own resource record by email match
  const myResource = useMemo(() => {
    if (canManage) return null
    return resources.find(r => r.email && user?.email && r.email.toLowerCase() === user.email.toLowerCase()) ?? null
  }, [resources, user, canManage])

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<TimeOffRequest | null>(null)
  const [form, setForm] = useState<FormState>(BLANK_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Reject modal state
  const [rejectTarget, setRejectTarget] = useState<TimeOffRequest | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejecting, setRejecting] = useState(false)

  const [filterResource, setFilterResource] = useState('')
  const [filterStatus, setFilterStatus] = useState<TimeOffStatus | ''>('')
  const [filterType, setFilterType] = useState<TimeOffType | ''>('')

  // The manager who will approve requests for the currently selected resource
  const approverForSelectedResource = useMemo(() => {
    if (!form.resource_id) return null
    const resource = resources.find(r => r.id === form.resource_id)
    if (!resource) return null
    if (resource.manager_id) {
      return orgManagers.find(m => m.user_id === resource.manager_id) ?? null
    }
    return null // fallback: all managers
  }, [form.resource_id, resources, orgManagers])

  const openAdd = () => {
    setEditing(null)
    const defaultResource = !canManage && myResource ? myResource.id : ''
    setForm({ ...BLANK_FORM, resource_id: defaultResource })
    setSaveError('')
    setShowModal(true)
  }

  const openEdit = (req: TimeOffRequest) => {
    setEditing(req)
    setForm({
      resource_id: req.resource_id,
      time_off_type: req.time_off_type,
      start_date: toDateOnly(req.start_date),
      end_date: toDateOnly(req.end_date),
      notes: req.notes,
      status: req.status,
    })
    setSaveError('')
    setShowModal(true)
  }

  const handleApprove = async (req: TimeOffRequest) => {
    if (!canManage) return
    try {
      await dataService.updateTimeOffRequest(req.id, {
        status: 'approved',
        reviewed_by: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
        rejection_reason: '',
      })
      await refreshData()
    } catch (err: any) {
      alert(err?.message || 'Failed to approve request')
    }
  }

  const openReject = (req: TimeOffRequest) => {
    setRejectTarget(req)
    setRejectionReason('')
  }

  const handleConfirmReject = async () => {
    if (!rejectTarget || rejecting) return
    setRejecting(true)
    try {
      await dataService.updateTimeOffRequest(rejectTarget.id, {
        status: 'rejected',
        reviewed_by: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
        rejection_reason: rejectionReason.trim(),
      })
      setRejectTarget(null)
      setRejectionReason('')
      await refreshData()
    } catch (err: any) {
      alert(err?.message || 'Failed to reject request')
    } finally {
      setRejecting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this time-off request?')) return
    try {
      await dataService.deleteTimeOffRequest(id)
      await refreshData()
    } catch (err: any) {
      alert(err?.message || 'Failed to delete request')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    if (form.end_date < form.start_date) {
      setSaveError('End date must be on or after start date')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editing) {
        await dataService.updateTimeOffRequest(editing.id, {
          resource_id: form.resource_id,
          time_off_type: form.time_off_type,
          start_date: form.start_date,
          end_date: form.end_date,
          notes: form.notes,
          status: canManage ? form.status : editing.status,
        })
      } else {
        await dataService.createTimeOffRequest(orgId, {
          resource_id: form.resource_id,
          time_off_type: form.time_off_type,
          start_date: form.start_date,
          end_date: form.end_date,
          notes: form.notes,
          rejection_reason: '',
          status: canManage ? form.status : 'pending',
          created_by: orgMember?.id ?? null,
          reviewed_by: null,
          reviewed_at: null,
        })
      }
      setShowModal(false)
      await refreshData()
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const activeResources = useMemo(() => {
    if (!canManage && myResource) return [myResource]
    return resources.filter(r => r.active)
  }, [resources, canManage, myResource])

  const filtered = useMemo(() => {
    return timeOffRequests
      .filter(r => {
        // Non-managers only see their own resource's requests
        if (!canManage && myResource && r.resource_id !== myResource.id) return false
        if (filterResource) {
          const res = resources.find(re => re.id === r.resource_id)
          if (!res?.title.toLowerCase().includes(filterResource.toLowerCase())) return false
        }
        if (filterStatus && r.status !== filterStatus) return false
        if (filterType && r.time_off_type !== filterType) return false
        return true
      })
      .sort((a, b) => b.start_date.localeCompare(a.start_date))
  }, [timeOffRequests, resources, filterResource, filterStatus, filterType, canManage, myResource])

  const summaryCounts = useMemo(() => {
    const base = canManage ? timeOffRequests : timeOffRequests.filter(r => myResource && r.resource_id === myResource.id)
    return {
      pending: base.filter(r => r.status === 'pending').length,
      approved: base.filter(r => r.status === 'approved').length,
      rejected: base.filter(r => r.status === 'rejected').length,
    }
  }, [timeOffRequests, canManage, myResource])

  const subtitle = canManage
    ? summaryCounts.pending > 0
      ? `${summaryCounts.pending} pending request${summaryCounts.pending !== 1 ? 's' : ''} awaiting review`
      : `${timeOffRequests.length} request${timeOffRequests.length !== 1 ? 's' : ''} total`
    : myResource
      ? `Manage your time-off requests`
      : 'No resource profile linked to your account'

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Time Off</h2>
          <p className={s.pageSubtitle}>{subtitle}</p>
        </div>
        {(canManage || myResource) && (
          <button className={s.primaryBtn} onClick={openAdd}>
            <Plus size={14} /> Add Request
          </button>
        )}
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {(['pending', 'approved', 'rejected'] as TimeOffStatus[]).map(status => {
          const count = summaryCounts[status]
          const { bg, color } = STATUS_COLORS[status]
          return (
            <button
              key={status}
              onClick={() => setFilterStatus(filterStatus === status ? '' : status)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                borderRadius: 20, border: `1.5px solid ${filterStatus === status ? color : 'transparent'}`,
                background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
            >
              {status === 'pending' ? <Clock size={13} /> : status === 'approved' ? <CheckCircle size={13} /> : <XCircle size={13} />}
              {count} {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          )
        })}
      </div>

      <div className={s.card}>
        <div className={s.filterBar}>
          {canManage && (
            <input
              className={s.filterInput}
              placeholder="Filter by resource..."
              value={filterResource}
              onChange={e => setFilterResource(e.target.value)}
            />
          )}
          <select
            className={s.filterSelect}
            value={filterType}
            onChange={e => setFilterType(e.target.value as TimeOffType | '')}
          >
            <option value="">All Types</option>
            {(Object.entries(TYPE_LABELS) as [TimeOffType, string][]).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select
            className={s.filterSelect}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as TimeOffStatus | '')}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <table className={s.table}>
          <thead>
            <tr>
              {canManage && <th>Resource</th>}
              <th>Type</th>
              <th>Start</th>
              <th>End</th>
              <th style={{ textAlign: 'right' }}>Days</th>
              <th>Status</th>
              {canManage && <th>Approver</th>}
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={canManage ? 9 : 6} className={s.emptyState}>No time-off requests found</td>
              </tr>
            )}
            {filtered.map(req => {
              const resource = resources.find(r => r.id === req.resource_id)
              const assignedManager = resource?.manager_id ? orgManagers.find(m => m.user_id === resource.manager_id) : null
              const { bg, color } = STATUS_COLORS[req.status]
              const days = daysBetween(req.start_date, req.end_date)
              const canEdit = canManage || (!canManage && req.status === 'pending')
              return (
                <tr key={req.id}>
                  {canManage && <td style={{ fontWeight: 600 }}>{resource?.title ?? '—'}</td>}
                  <td>{TYPE_LABELS[req.time_off_type]}</td>
                  <td>{formatDate(req.start_date)}</td>
                  <td>{formatDate(req.end_date)}</td>
                  <td style={{ textAlign: 'right' }}>{days}d</td>
                  <td>
                    <div>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                        fontSize: 12, fontWeight: 600, background: bg, color,
                      }}>
                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      </span>
                      {req.status === 'rejected' && req.rejection_reason && (
                        <div style={{ fontSize: 11, color: '#d13438', marginTop: 3, maxWidth: 160 }} title={req.rejection_reason}>
                          {req.rejection_reason.length > 50 ? req.rejection_reason.slice(0, 50) + '…' : req.rejection_reason}
                        </div>
                      )}
                    </div>
                  </td>
                  {canManage && (
                    <td style={{ fontSize: 12, color: assignedManager ? '#323130' : '#a19f9d' }}>
                      {assignedManager ? `${assignedManager.display_name}${assignedManager.title ? ` — ${assignedManager.title}` : ''}` : 'Any manager'}
                    </td>
                  )}
                  <td style={{ color: '#605e5c', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {req.notes || '—'}
                  </td>
                  <td>
                    <div className={s.actions}>
                      {canManage && req.status === 'pending' && (
                        <>
                          <button
                            className={`${s.actionBtn} ${s.edit}`}
                            onClick={() => handleApprove(req)}
                            title="Approve"
                            style={{ color: '#107c10' }}
                          >
                            <CheckCircle size={14} />
                          </button>
                          <button
                            className={`${s.actionBtn} ${s.del}`}
                            onClick={() => openReject(req)}
                            title="Reject"
                            style={{ color: '#d13438' }}
                          >
                            <XCircle size={14} />
                          </button>
                        </>
                      )}
                      {canEdit && (
                        <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(req)} title="Edit">
                          <EditIcon size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(req.id)} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Rejection reason modal */}
      <Modal
        open={!!rejectTarget}
        title="Reject Time-Off Request"
        onClose={() => setRejectTarget(null)}
        footer={
          <>
            <button className={s.secondaryBtn} onClick={() => setRejectTarget(null)}>Cancel</button>
            <button
              className={s.primaryBtn}
              style={{ background: '#d13438' }}
              onClick={handleConfirmReject}
              disabled={rejecting}
            >
              {rejecting ? 'Rejecting...' : 'Confirm Reject'}
            </button>
          </>
        }
      >
        {rejectTarget && (
          <div>
            <p style={{ fontSize: 14, color: '#605e5c', marginBottom: 16 }}>
              Rejecting time-off request for <strong>{resources.find(r => r.id === rejectTarget.resource_id)?.title}</strong>
              {' '}({formatDate(rejectTarget.start_date)} – {formatDate(rejectTarget.end_date)})
            </p>
            <div className={s.formGroup}>
              <label>Reason for rejection (optional)</label>
              <textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                rows={3}
                placeholder="Briefly explain why this request is being rejected..."
                style={{ resize: 'vertical' }}
                autoFocus
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Add / Edit modal */}
      <Modal
        open={showModal}
        title={editing ? 'Edit Time-Off Request' : 'Add Time-Off Request'}
        onClose={() => setShowModal(false)}
        footer={
          <>
            <button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
            <button className={s.primaryBtn} form="timeOffForm" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        <form id="timeOffForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}

          <div className={s.formGroup}>
            <label>Resource *</label>
            {!canManage && myResource ? (
              <input value={myResource.title} disabled style={{ background: '#f3f2f1', color: '#605e5c' }} />
            ) : (
              <select
                value={form.resource_id}
                onChange={e => setForm(f => ({ ...f, resource_id: e.target.value }))}
                required
              >
                <option value="">Select resource...</option>
                {activeResources.map(r => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
            )}
          </div>

          {/* Routing hint: show who will review this request */}
          {form.resource_id && !editing && (
            <div style={{
              fontSize: 12, padding: '8px 12px', borderRadius: 6, marginBottom: 12,
              background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1',
            }}>
              {approverForSelectedResource
                ? `Will be reviewed by: ${approverForSelectedResource.display_name}`
                : 'Will be sent to all managers for approval'}
            </div>
          )}

          <div className={s.formGroup}>
            <label>Type *</label>
            <select
              value={form.time_off_type}
              onChange={e => setForm(f => ({ ...f, time_off_type: e.target.value as TimeOffType }))}
              required
            >
              {(Object.entries(TYPE_LABELS) as [TimeOffType, string][]).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className={s.formGroup}>
              <label>Start Date *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                required
              />
            </div>
            <div className={s.formGroup}>
              <label>End Date *</label>
              <input
                type="date"
                value={form.end_date}
                min={form.start_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                required
              />
            </div>
          </div>

          {form.start_date && form.end_date && form.end_date >= form.start_date && (
            <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 12, marginTop: -4 }}>
              {daysBetween(form.start_date, form.end_date)} calendar day{daysBetween(form.start_date, form.end_date) !== 1 ? 's' : ''}
            </div>
          )}

          {canManage && (
            <div className={s.formGroup}>
              <label>Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value as TimeOffStatus }))}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          )}

          <div className={s.formGroup}>
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Optional reason or description..."
              style={{ resize: 'vertical' }}
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}
